import {Switch} from '@wandb/weave/components';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Dropdown, Input} from 'semantic-ui-react';

import {envIsServerDeployment} from '../config';
import {useViewer} from '../state/viewer/hooks';
import {isLegendPosition} from '../util/plotHelpers/legend';
import {isFontSizeOrAuto} from '../util/plotHelpers/plotFontSize';
import {type Line} from '../util/plotHelpers/types';
import {type Query} from '../util/queryTypes';
import {LabeledOptionBlock as LabeledOption} from './elements/LabeledOptionBlock';
import MultiButton from './elements/MultiButton';
import {type BarChartConfig} from './PanelBarChart/types';
import {usePanelConfigContext} from './PanelRunsLinePlot/PanelConfigContext';
import {
  type DeltaQueryTypeOverride,
  type RunsLinePlotConfig,
} from './PanelRunsLinePlot/types';
import {
  isSinglePointChart,
  resolveSinglePointRenderMode,
} from './PanelRunsLinePlot/utils/checkTypes';
import {SINGLE_POINTS_RENDER_OPTIONS} from './WorkspaceDrawer/Settings/runLinePlots/singlePointsRenderOptions';

type PanelConfig = RunsLinePlotConfig | BarChartConfig;

interface PanelChartOptionsProps {
  config: PanelConfig;
  defaultTitle?: string;
  pageQuery: Query; // for the field selector
  showLegend?: boolean;
  type: 'lines' | 'bars' | 'scalar';
  updateConfig: (newConfig: PanelConfig) => void;
  lines?: Line[];
}

type DebouncedInputOptionProps = {
  label?: string;
  value?: string;
  update: (newValue: string) => void;
  placeholder?: string;
};

export function DebouncedInputOption({
  label,
  value = '',
  update,
  placeholder = '',
}: DebouncedInputOptionProps) {
  const [displayedValue, setDisplayedValue] = useState(value);
  const debouncedUpdate = useMemo(() => debounce(update, 200), [update]);
  // if value prop changes, sync to displayedValue state
  useEffect(() => {
    setDisplayedValue(value);
  }, [value]);
  const input = (
    <Input
      placeholder={placeholder}
      value={displayedValue}
      onChange={(e, {value: newValue}) => {
        setDisplayedValue(newValue);
        debouncedUpdate(newValue);
      }}
    />
  );
  return label ? <LabeledOption label={label} option={input} /> : input;
}

const fontSizeOptions = ['small', 'medium', 'large', 'auto'];
const fontSizeLabels = ['Small', 'Medium', 'Large', 'Auto'];

const legendPositionOptions = ['north', 'south', 'east', 'west'];
const legendPositionLabels = ['North', 'South', 'East', 'West'];

const deltaQueryTypeOptions: {
  key: string;
  text: string;
  value: DeltaQueryTypeOverride;
}[] = [
  {key: 'bucketed', text: 'Bucketed Query', value: 'bucketed'},
  {key: 'backendLines', text: 'Backend Lines Query', value: 'backendLines'},
];

const PanelChartOptions = (props: PanelChartOptionsProps) => {
  const {config, showLegend, updateConfig} = props;
  const viewer = useViewer();
  const isAdmin = !envIsServerDeployment && viewer?.admin;
  const {forceReactVis, deltaQueryTypeOverride, singlePointRenderMode} =
    usePanelConfigContext();

  const handleChartTitleUpdate = useCallback(
    (value: string) => updateConfig({chartTitle: value}),
    [updateConfig]
  );

  const showSinglePointRenderOption =
    props.type === 'lines' &&
    props.lines != null &&
    isSinglePointChart(props.lines);
  return (
    <Tailwind>
      {props.type === 'bars' && (
        <LabeledOption
          label="Chart title"
          labelWidth="140px"
          option={
            <DebouncedInputOption
              value={config.chartTitle}
              update={handleChartTitleUpdate}
              placeholder={props.defaultTitle}
            />
          }
        />
      )}
      {isAdmin && (
        <LabeledOption
          label="Force react-vis (W&B ADMIN ONLY)"
          labelWidth="140px"
          option={
            <Switch.Root
              size="medium"
              checked={forceReactVis}
              onCheckedChange={isChecked =>
                updateConfig({forceReactVis: isChecked})
              }>
              <Switch.Thumb size="medium" checked={forceReactVis ?? false} />
            </Switch.Root>
          }
        />
      )}
      {isAdmin && props.type === 'lines' && (
        <LabeledOption
          label="Delta Query Type (W&B ADMIN ONLY)"
          labelWidth="140px"
          option={
            <div className="flex items-center gap-8">
              <Dropdown
                selection
                placeholder="Default"
                value={deltaQueryTypeOverride ?? ''}
                options={deltaQueryTypeOptions}
                onChange={(e, {value}) =>
                  updateConfig({
                    deltaQueryTypeOverride:
                      value === 'bucketed' || value === 'backendLines'
                        ? value
                        : undefined,
                  })
                }
              />
              {deltaQueryTypeOverride != null && (
                <Button
                  aria-label="Reset"
                  icon="randomize-reset-reload"
                  onClick={() =>
                    updateConfig({deltaQueryTypeOverride: undefined})
                  }
                  size={ButtonSizes.Medium}
                  tooltip="Reset"
                  variant={ButtonVariants.Ghost}
                />
              )}
            </div>
          }
        />
      )}
      {(props.type === 'lines' || props.type === 'scalar') && (
        <LabeledOption
          label="Show legend"
          labelWidth="140px"
          option={
            <Switch.Root
              size="medium"
              checked={showLegend}
              onCheckedChange={isChecked =>
                updateConfig({showLegend: isChecked})
              }>
              <Switch.Thumb size="medium" checked={showLegend ?? false} />
            </Switch.Root>
          }
        />
      )}
      {showSinglePointRenderOption && (
        <LabeledOption
          label="Single-point metrics"
          labelWidth="140px"
          option={
            <MultiButton
              ariaLabel="Select single-point metrics"
              keys={SINGLE_POINTS_RENDER_OPTIONS.map(option => option.value)}
              labels={SINGLE_POINTS_RENDER_OPTIONS.map(option => option.label)}
              selected={resolveSinglePointRenderMode(
                props.lines ?? [],
                singlePointRenderMode
              )}
              onChange={key => {
                if (key === 'scatter' || key === 'bar') {
                  updateConfig({singlePointRenderMode: key});
                }
              }}
            />
          }
        />
      )}
      {(props.type === 'lines' || props.type === 'bars') && (
        <>
          <LabeledOption
            label="Font size"
            labelWidth="140px"
            option={
              <MultiButton
                ariaLabel="Select font size"
                keys={fontSizeOptions}
                labels={fontSizeLabels}
                selected={config.fontSize ?? 'auto'}
                onChange={key => {
                  if (isFontSizeOrAuto(key)) {
                    updateConfig({
                      fontSize: key,
                    });
                  }
                }}
              />
            }
          />
        </>
      )}
      {props.type === 'lines' && (
        <>
          <LabeledOption
            label="Legend position"
            helpText="Placement of the chart legend."
            labelWidth="140px"
            option={
              <MultiButton
                ariaLabel="Select legend position"
                keys={legendPositionOptions}
                labels={legendPositionLabels}
                selected={
                  (config as RunsLinePlotConfig).legendPosition ?? 'north'
                }
                onChange={key => {
                  if (isLegendPosition(key)) {
                    updateConfig({legendPosition: key});
                  }
                }}
              />
            }
          />
        </>
      )}
    </Tailwind>
  );
};

export default PanelChartOptions;
